import { action, computed, makeObservable, observable } from 'mobx';

import { RootStore } from '.';
import { jackpotSocket } from '../api';
import { JackpotTierName, NotificationPriority, SideMenuIcons } from '../enums';
import { JackpotData, JackpotUpdateModel, JackpotWinModel, JackpotWinnersModel, JackpotWinPopupModel } from '../models';
import { JACK_POT_AMOUNT_KEY, ValidationType } from '../jackpot/constants';
import { getSecondsDifference, loadResources } from '../jackpot/helpers';
import { jackpotResources } from '../jackpot/modules/jackpotModule/constants';
import { soundOptions, soundService, SoundTypes } from '../services/soundService';
import { dotFormatter, setTranslationValues } from '../helpers';
import { T } from '../constants';

export class JackpotStore {

  private rootStore: RootStore;

  public jackPotData: any[] = [];

  public jackpot: JackpotUpdateModel = { name: '', tiers: [] };

  public jackpotWinners: Partial<Record<JackpotTierName, JackpotWinnersModel>> = {};

  public openJackpotScene: boolean = false;

  public isShowJackpotCongratsPopUp: boolean = false;

  public openJackpotSceneAfterRoundFinished: boolean = false;

  public shouldPlayInitialJackpotMusic: boolean = false;

  public winCard = null;

  public jackPotExpireTime: number = 300000;
  public jackPotExpireDate: string | null = null;

  public remainingJackpotCards: any[] = [];

  public isAllowDoJackpotAction: boolean = true;

  public isExpiredJackpotCards: boolean = false;

  public openedJackpotCardCount: { [key: string]: number } = { 1: 0, 2: 0, 3: 0, 4: 0 };

  public jackpotDetailsOpen: boolean = false;

  public winPopupData: null | JackpotWinPopupModel = null;
  public isWinMoveAndScaleSymbolComplete: boolean = false;
  public hasJackPot: boolean = false;
  public jackpotAmounts: any = {
    name: null,
    clubAmount: 0,
    heartAmount: 0,
    spadeAmount: 0,
    diamondAmount: 0,
  };
  public jackpotSceneAmounts: any = {
    name: null,
    clubAmount: 0,
    heartAmount: 0,
    spadeAmount: 0,
    diamondAmount: 0,
  };
  isShowJackPotWinPopup: boolean = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this, {

      reSetJackpotData: action.bound,

      isShowJackPotWinPopup: observable,
      setIsShowJackPotWinPopup: action.bound,

      isWinMoveAndScaleSymbolComplete: observable,
      resetOpenedJackpotCardCount: action.bound,
      setIsWinMoveAndScaleSymbolComplete: action.bound,

      openedJackpotCardCount: observable,
      setOpenedJackpotCardCount: action.bound,

      jackPotExpireTime: observable,
      setJackPotExpireTime: action.bound,
      jackPotExpireDate: observable,
      setJackPotExpireDate: action.bound,

      jackpotAmounts: observable,
      setJackpotAmounts: action.bound,
      updateJackpotAmounts: action.bound,

      jackpotSceneAmounts: observable,

      isExpiredJackpotCards: observable,
      setIsExpiredJackpotCards: action.bound,

      openJackpotSceneAfterRoundFinished: observable,
      setOpenJackpotSceneAfterRoundFinished: action.bound,

      shouldPlayInitialJackpotMusic: observable,
      setShouldPlayInitialJackpotMusic: action.bound,

      remainingJackpotCards: observable,
      showRemainingJackpotCards: action.bound,

      jackPotData: observable,
      setJackPotData: action.bound,
      updateJackpotData: action.bound,

      isShowJackpotCongratsPopUp: observable,
      setShowJackpotCongratsPopUp: action.bound,

      isAllowDoJackpotAction: observable,
      setIsAllowDoJackpotAction: action.bound,

      winCard: observable,
      setWinCard: action.bound,

      openJackpotScene: observable,
      setOpenJackpotScene: action.bound,

      hasJackPot: observable,
      setHasJackPot: action.bound,

      jackpot: observable,
      setJackpot: action.bound,

      jackpotWinners: observable,
      setJackpotWinners: action.bound,

      winPopupData: observable,
      setWinPopupData: action.bound,

      jackpotDetailsOpen: observable,
      setJackpotDetailsOpen: action.bound,

      jakpotMap: computed
    });
  }

  public setJackpot(value: JackpotUpdateModel): void {
    this.jackpot = value;
  }

  public setJackPotData(jackPotData: any) {
    this.jackPotData = jackPotData;

  };

  public setJackpotAmounts(jackpotAmounts: any) {
    this.jackpotAmounts = jackpotAmounts;
    this.jackpotSceneAmounts = this.jackpotAmounts;
  };

  public setOpenedJackpotCardCount(id: number): number {
    const currentCount = this.openedJackpotCardCount[id] ?? 0;
    this.openedJackpotCardCount[id] = currentCount + 1;
    return currentCount;
  };

  public updateJackpotAmounts(jackpotWinCard: any) {
    this.jackpotAmounts[JACK_POT_AMOUNT_KEY[jackpotWinCard.jackpotCard]] = 0;
    this.jackpotSceneAmounts[JACK_POT_AMOUNT_KEY[jackpotWinCard.jackpotCard]] = 0;
  };

  public setIsAllowDoJackpotAction(isAllowDoJackpotAction: boolean) {
    this.isAllowDoJackpotAction = isAllowDoJackpotAction;
  }

  public setIsExpiredJackpotCards(bool: boolean) {
    this.isExpiredJackpotCards = bool;
  };

  public setJackpotWinners(value: Partial<Record<JackpotTierName, JackpotWinnersModel>>): void {
    this.jackpotWinners = value;
  }

  public setJackpotDetailsOpen(value: boolean): void {
    this.jackpotDetailsOpen = value;
  }

  public setWinPopupData(value: null | JackpotWinPopupModel): void {
    this.winPopupData = value;
    if (value) {
      this.invokeJackpotWinSeen();
    }
  }

  public setOpenJackpotScene(value: boolean): void {
    this.openJackpotScene = value;
    this.jackpotSceneAmounts = value && this.jackpotAmounts;

  }

  public setShowJackpotCongratsPopUp(value: boolean): void {
    this.isShowJackpotCongratsPopUp = value;
  }

  public setOpenJackpotSceneAfterRoundFinished(value: boolean): void {
    this.openJackpotSceneAfterRoundFinished = value;
  }

  public setShouldPlayInitialJackpotMusic(value: boolean): void {
    this.shouldPlayInitialJackpotMusic = value;
  }

  public setWinCard(cardNumber: any) {
    this.winCard = cardNumber;
  }

  public setIsWinMoveAndScaleSymbolComplete(isWinMoveAndScaleSymbolComplete: boolean) {
    this.isWinMoveAndScaleSymbolComplete = isWinMoveAndScaleSymbolComplete;
  };

  public setHasJackPot(hasJackPot: boolean) {
    this.hasJackPot = hasJackPot;
  };

  public setJackPotExpireTime(jackPotExpireTime: any) {
    this.jackPotExpireTime = jackPotExpireTime;
  };

  public setJackPotExpireDate(jackPotExpireDate: string | null) {
    this.jackPotExpireDate = jackPotExpireDate;
  };

  public setIsShowJackPotWinPopup(isShowJackPotWinPopup: boolean) {
    this.isShowJackPotWinPopup = isShowJackPotWinPopup;
  };
  public resetWinPopupData(): void {
    this.setWinPopupData(null);
  }

  public resetOpenedJackpotCardCount() {
    Object.keys(this.openedJackpotCardCount).forEach((key) => {
      this.openedJackpotCardCount[key] = 0;
    });
  };

  public get jakpotMap(): Record<JackpotTierName, JackpotData> {
    return this.jackpot.tiers.reduce((acc, tier) => {
      acc[tier.name] = tier;
      return acc;
    }, {} as Record<JackpotTierName, JackpotData>);
  }

  loadJackpotResources = async (): Promise<void> => {
    await loadResources(jackpotResources)
    soundService.setSoundResources(soundOptions);
    await soundService.loadSoundResources();
  };

  public openJackpotSceneAmountSetter() {
    const mapped: any = {
      name: this.jackpot.name,
      clubAmount: 0,
      diamondAmount: 0,
      heartAmount: 0,
      spadeAmount: 0,
    };

    this.jackpot.tiers?.forEach((tier: any) => {
      const key = `${tier.name.toLowerCase()}Amount`;
      if (key in mapped) {
        mapped[key] = tier.amount; // or tier.coinAmount
      }
    });

    this.jackpotAmounts = mapped;
    this.jackpotSceneAmounts = { ...mapped };
  }
  public handleJackpotUpdate(data: JackpotUpdateModel): void {
    this.setJackpot(data);
  }

  public handleJackpotWin(data: JackpotWinModel): void {
    const { secretId, winAmount } = data;
    const { currencySymbol } = this.rootStore.gameStore;
    this.rootStore.sidebarMenuStore.setHasPromotionsNotification(true);
    this.rootStore.sidebarNotificationStore.enqueue(
      `${setTranslationValues(this.rootStore.translationsStore.translate(T.PLAYER_HAS_WON_JACKPOT), { secretId, winAmount: `${dotFormatter(winAmount, 2)} ${currencySymbol}` })}`,
      SideMenuIcons.Jackpot,
      NotificationPriority.High
    );
  }

  public handleJackpotWinnersUpdate(data: Array<JackpotWinnersModel>): void {
    const winnersMap = data.reduce((acc, tier) => {
      acc[tier.name] = tier;
      return acc;
    }, {} as Record<JackpotTierName, JackpotWinnersModel>);
    this.setJackpotWinners(winnersMap);
  }

  public handleLastJackpotWin(data: JackpotWinPopupModel): void {
    this.setWinPopupData(data);
  }

  public updateJackpotData(data: any, isExpireTime: boolean = false) {

    const infoData = isExpireTime
      ? [...data]
      : this.jackPotData.some((item) => item.index === data.jackpotSceneItem?.index)
        ? this.jackPotData.map((item) => item.index === data.jackpotSceneItem?.index ? data.jackpotSceneItem : item)
        : [...this.jackPotData, data.jackpotSceneItem];

    this.remainingJackpotCards = isExpireTime ? data : data.jackpotSceneItems;
    this.jackPotData = infoData;
  };

  public showRemainingJackpotCards(isExpireTime: boolean = false) {
    this.jackPotData = isExpireTime ? [...this.remainingJackpotCards] : [...this.jackPotData, ...this.remainingJackpotCards]
  };

  public handleOpenJackpotRemainingScene(): void {
    jackpotSocket.invokeOpenJackpotRemainingScene();
  }

  public handleOpenJackpotScene(data: any) {
    this.setJackPotExpireDate(data.expireDate);
    this.setJackPotExpireTime(data.expireDate ? getSecondsDifference(data.expireDate) : 300000);
    if (this.rootStore.gameStore.roundInProgress) {
      this.setOpenJackpotSceneAfterRoundFinished(true);
    } else {
      this.showJackpotScene();
    }
  }

  public showJackpotScene() {
    soundService.play(SoundTypes.JackpotSceneTransition);
    this.setShowJackpotCongratsPopUp(true);
    this.rootStore.postMessageStore.sendPauseGame();
  }
  public invokeOpenJackpotSceneItem(index: number) {
    jackpotSocket.invokeOpenJackpotSceneItem(index)
  }

  public invokeJackpotWinSeen() {
    jackpotSocket.invokeJackpotWinSeen();
  }

  handleOpenJackpotSceneInitial(data: any) {
    const { jackpotCardResults, openScene } = data;
    const expireDate = data?.value;

    this.setShouldPlayInitialJackpotMusic(!!openScene);
    this.setShowJackpotCongratsPopUp(false);

    this.setOpenJackpotScene(openScene);

    if (jackpotCardResults) {
      this.setJackPotData(
        jackpotCardResults
          .filter((item: any) => item.isOpened === true)
          .map((item: any) => ({
            ...item,
            // isEnd: true forces GSAP duration to 0 so they snap instantly on refresh
            isEnd: true
          }))
      );
    }

    this.setJackPotExpireDate(expireDate);
    this.setJackPotExpireTime(expireDate ? getSecondsDifference(expireDate) : 300000);
  }


  handleJackpotSceneItemResult(data: any) {
    if (data.validation === ValidationType.Success) {
      if (data.jackpotSceneItem.isWin) {
        this.updateJackpotAmounts(data.jackpotSceneItem);
        this.setIsAllowDoJackpotAction(false);
        setTimeout(() => {
          this.setIsExpiredJackpotCards(true);
          this.showRemainingJackpotCards();
        }, 1000);
      }
      this.updateJackpotData(data);
    } else {
      this.setIsAllowDoJackpotAction(true);
    }
  }
  handleOpenJackpotSceneResult(data: any) {
    if (!data || data.validation !== ValidationType.Success) return;

    const sceneItems: any[] =
      (Array.isArray(data.jackpotSceneItemInfos) && data.jackpotSceneItemInfos)
      || (Array.isArray(data.jackpotSceneItems) && data.jackpotSceneItems)
      || [];

    const winnerItem = sceneItems.find((item: any) => item?.isWin);
    const winCard = winnerItem?.jackpotCard ?? null;

    this.setIsExpiredJackpotCards(true);
    if (winCard !== null) {
      this.setWinCard(winCard);
    }

    const expireDate = data.expireDate;
    this.setJackPotExpireDate(expireDate);
    this.setJackPotExpireTime(expireDate ? getSecondsDifference(expireDate) : this.jackPotExpireTime);

    this.updateJackpotData(sceneItems, true);
    this.showRemainingJackpotCards(true);

    if (winCard !== null) {
      setTimeout(() => {
        this.setIsShowJackPotWinPopup(true);
      }, 2000);
    }
  }

  reSetJackpotData() {
    this.setWinCard(null);
    this.setJackPotData([]);
    this.setJackPotExpireDate(null);
    this.setOpenJackpotScene(false);
    this.setJackPotExpireTime(300000);
    this.setIsAllowDoJackpotAction(true);
    // Stop all jackpot-related sounds on scene close
    soundService.stop(SoundTypes.JackpotSceneTransition);
    soundService.stop(SoundTypes.JackpotBackgroundMusic);
    soundService.stop(SoundTypes.JackpotSuitAppearance);
    soundService.stop(SoundTypes.JackpotSuitTransfer);
    soundService.stop(SoundTypes.JackpotWinPopUp);
    soundService.stop(SoundTypes.JackpotSymbolSound);
    this.setIsShowJackPotWinPopup(false);
    this.setShouldPlayInitialJackpotMusic(false);
    this.setIsWinMoveAndScaleSymbolComplete(false);
    this.rootStore.postMessageStore.sendResumeGame();
    this.rootStore.postMessageStore.sendUpdateBalance();
    this.setIsExpiredJackpotCards(false);
  };

  public listenEvents(): void {
    jackpotSocket.onOpenJackpotSceneInitial(this.handleOpenJackpotSceneInitial.bind(this));
    jackpotSocket.onOpenJackpotSceneResult(this.handleOpenJackpotSceneResult.bind(this));
    jackpotSocket.onOpenJackpotScene(this.handleOpenJackpotScene.bind(this));
    jackpotSocket.onOpenJackpotSceneItemResut(this.handleJackpotSceneItemResult.bind(this));
    jackpotSocket.onJackpotWinnerResult(this.handleJackpotWinnersUpdate.bind(this));
    jackpotSocket.onLastJackpotWin(this.handleLastJackpotWin.bind(this));
    jackpotSocket.onJackpotUpdate(this.handleJackpotUpdate.bind(this));
    jackpotSocket.onJackpotWin(this.handleJackpotWin.bind(this));
  }

}
